'use strict';

const libPictProvider = require('pict-provider');
const libEntityPickerSearch = require('../services/Entity-Picker-Search.js');

/**
 * SamplePlanDashboard — a net-new pict recreation of the legacy "Material LineItem Samples" dashboard
 * (web/app_dashboards/.../materials_samples). It presents the project's sampling data through a pill tab bar of
 * PRESETS, each a read grid over a shared bundle:
 *   • Sample Plan     — line-item folders → one row per ProjectLineItemMaterialTest (Material, Type, Rate, Frequency,
 *                       Bid/Installed Qty, Planned/Required Samples).
 *   • Samples         — line-item folders → material sub-groups → one row per SampleType (Units, Type, Rate, Frequency,
 *                       Conversion Factor, Approved Source, Special Instructions).
 *   • Failing Samples — a flat list of every actual Sample with Status FAIL (Sample name, Units, Type, Disposition).
 *
 * SHAREABLE: this provider owns the fetch (the lean SamplePlanBundle/Parts endpoint — a single streamed request for
 * only the parts a preset needs), the bundle→rows transforms (which reproduce the legacy SPBundle shaping row-for-row
 * from the raw parts), and the host-div paint. Any host embeds it with `provider.openInto(idProject, '#SomeHost')`;
 * the standalone #/SamplePlanDashboard screen uses the thin SamplePlanDashboard view. The provider never assumes an
 * entity name — it reads config off `this.cfg`.
 */
class SamplePlanDashboardProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._idProject = 0;
		this._mount = '';
		this._rawBundle = null;   // the last-fetched bundle parts (a superset once samples are loaded)
		this._haveSamples = false;// whether _rawBundle includes the Samples/SampleLineItemJoins parts
		this._model = null;       // uniform view model — see _buildForPreset
		this._preset = 'SamplePlan';
		this._search = '';
		this._sortBy = 'Material';
		this._sortDesc = false;
		this._editingSample = 0;  // Failing Samples: IDSample whose disposition is being edited inline (0 = none)
		this._savingSample = 0;   // IDSample whose disposition PUT is in flight
		this._customGroup = 'None';                                            // Custom: active grouping
		this._customColumns = SamplePlanDashboardProvider.CUSTOM_DEFAULT_COLUMNS.slice();  // Custom: chosen column keys
		this._fullscreen = false;  // standalone "full screen" — overlay the whole browser view (see toggleFullscreen)

		if (typeof document !== 'undefined')
		{
			// Esc leaves full screen (routed through toggleFullscreen).
			this._onKeydown = (pEvent) => { if (pEvent.key === 'Escape' && this._fullscreen) { this.toggleFullscreen(); } };
			document.addEventListener('keydown', this._onKeydown);
		}

		if (this.pict && this.pict.CSSMap && (typeof this.pict.CSSMap.addCSS === 'function'))
		{
			// Idempotent key — multiple instances register the same block harmlessly.
			this.pict.CSSMap.addCSS('SamplePlanDashboard-CSS', _CSS, 530);
		}
	}

	// --- config + services ---
	get cfg() { return this.options || {}; }
	get providerName() { return this.cfg.ProviderIdentifier || 'SamplePlanDashboard'; }
	get restClient() { return (this.pict.fable && this.pict.fable.HeadlightRestClient) || this.pict.HeadlightRestClient; }
	/** Show the per-row "New Sample" action column (off by default, mirroring the legacy dashboard's default). */
	get showAddSample() { return this.cfg.ShowAddSample === true; }
	get presets() { return SamplePlanDashboardProvider.PRESETS; }
	get activePreset() { return this.presets.find((pPreset) => pPreset.Key === this._preset) || this.presets[0]; }
	/** True when painted into a host OTHER than the standalone screen (e.g. the embedded Project-workspace tab), where a
	 *  "Full screen" pop-out to the standalone full-width dashboard is offered. */
	get isEmbedded() { return !!this._mount && this._mount !== '#SamplePlanDashboard-Host'; }

	get state()
	{
		const tmpKey = this.providerName;
		if (!this.pict.AppData[tmpKey]) { this.pict.AppData[tmpKey] = { Loading: false, Loaded: false, Error: '', IDProject: 0, ProjectName: '', RowCount: 0 }; }
		return this.pict.AppData[tmpKey];
	}

	_getJSON(pURL) { return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody))); }
	_postJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pBody, (pError, pResult) => pError ? reject(pError) : resolve(pResult))); }
	_putJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.putJSON(pURL, pBody, (pError, pResult) => pError ? reject(pError) : resolve(pResult))); }
	_esc(pValue) { return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }
	_num(pValue) { const tmp = Number(pValue); return Number.isFinite(tmp) ? Math.round(tmp).toLocaleString() : ''; }
	_isZero(pValue) { const tmp = Number(pValue); return !pValue || (Number.isFinite(tmp) && tmp === 0); }

	/** The bundle parts a preset needs — lean by default; the sample-record presets add Samples + SampleLineItemJoins. */
	_partsFor(pPreset) { return (pPreset && pPreset.NeedsSamples) ? SamplePlanDashboardProvider.BUNDLE_PARTS.concat(['Samples', 'SampleLineItemJoins']) : SamplePlanDashboardProvider.BUNDLE_PARTS; }

	// --- Shareable mount seam ---

	/**
	 * Render the dashboard for a project into a host element. Safe to call on every host repaint — reloads only when
	 * the project changes; otherwise repaints from cache.
	 * @param {number|string} pIDProject @param {string} pMountSelector
	 */
	openInto(pIDProject, pMountSelector)
	{
		this._mount = pMountSelector || this.cfg.MountSelector || '#SamplePlanDashboard-Host';
		const tmpID = Number(pIDProject) || 0;
		if (tmpID && (tmpID !== this._idProject)) { this.loadAsync(tmpID); }
		else { this.render(); }
	}

	// --- Load + shape ---

	async loadAsync(pIDProject)
	{
		this._idProject = Number(pIDProject) || 0;
		const tmpState = this.state;
		tmpState.Loading = true; tmpState.Loaded = false; tmpState.Error = ''; tmpState.IDProject = this._idProject;
		this._rawBundle = null; this._haveSamples = false; this._model = null; this._search = '';
		this.render();
		if (!this._idProject) { tmpState.Loading = false; this.render(); return; }
		try
		{
			// Single lean endpoint: SamplePlanBundle/Parts streams ONLY the parts a preset needs — ~85% smaller than the
			// full SamplePlanBundle (it omits Samples/TestData/TestInstances/… unless a preset asks for them). The server
			// refreshes the PLIMTs' Computed{Required,Planned}SampleCount via PLIMTCalculator, so the builders rebuild the
			// per-SampleType aggregate client-side and produce the same rows the full bundle's mlij.SampleTypes did.
			const tmpParts = this._partsFor(this.activePreset);
			this._rawBundle = (await this._postJSON(`SamplePlanBundle/Parts/By/IDProject/${this._idProject}`, { Parts: tmpParts })) || {};
			this._haveSamples = tmpParts.indexOf('Samples') >= 0;
			tmpState.ProjectName = (this._rawBundle.Project && (this._rawBundle.Project.Name || this._rawBundle.Project.Description)) || `Project ${this._idProject}`;
			this._rebuild();
			tmpState.Loaded = true;
		}
		catch (pError) { tmpState.Error = `Could not load the sample plan dashboard: ${pError && pError.message ? pError.message : pError}`; }
		tmpState.Loading = false;
		this.render();
	}

	/** Rebuild the view model for the active preset from the cached raw bundle, and refresh the row count. */
	_rebuild()
	{
		this._model = this._buildForPreset(this._rawBundle || {});
		this.state.RowCount = this._modelRowCount();
	}

	_modelRowCount() { return ((this._model && this._model.Sections) || []).reduce((pSum, pSection) => pSum + pSection.Groups.reduce((pG, pGroup) => pG + pGroup.Rows.length, 0), 0); }

	_buildForPreset(pBundle)
	{
		if (this._preset === 'Samples') { return this._buildSamples(pBundle); }
		if (this._preset === 'FailingSamples') { return this._buildFailingSamples(pBundle); }
		if (this._preset === 'Custom') { return this._buildCustom(pBundle); }
		return this._buildSamplePlan(pBundle);
	}

	/** Shared bundle indexes used by the builders. */
	_indexBundle(pBundle)
	{
		const tmpMaterialByID = {};
		(pBundle.Materials || []).forEach((pMaterial) => { tmpMaterialByID[pMaterial.IDMaterial] = pMaterial; });
		const tmpMLIJByLineItem = {};
		(pBundle.MaterialLineItemJoins || []).forEach((pMLIJ) => { (tmpMLIJByLineItem[pMLIJ.IDLineItem] = tmpMLIJByLineItem[pMLIJ.IDLineItem] || []).push(pMLIJ); });
		const tmpPLIMTByJoin = {};
		(pBundle.ProjectLineItemMaterialTests || []).forEach((pPLIMT) => { const tmpKey = `${pPLIMT.IDLineItem}_${pPLIMT.IDMaterial}`; (tmpPLIMTByJoin[tmpKey] = tmpPLIMTByJoin[tmpKey] || []).push(pPLIMT); });
		return { materialByID: tmpMaterialByID, mlijByLineItem: tmpMLIJByLineItem, plimtByJoin: tmpPLIMTByJoin };
	}

	_materialName(pMaterial, pIDMaterial) { return (pMaterial && pMaterial.Name) || `Material ${pIDMaterial}`; }
	_materialCode(pMaterial) { return (pMaterial && (pMaterial.MaterialCode || pMaterial.CatalogCode)) || ''; }
	_lineItemName(pLineItem) { return pLineItem.Name || pLineItem.ItemNumber || `Line Item ${pLineItem.IDLineItem}`; }

	/**
	 * Sample Plan preset — line-item folders → one row per ProjectLineItemMaterialTest (verified row-for-row against the
	 * full SamplePlanBundle). Grouping mirrors getSamplePlanBundleData: group PLIMTs by `${IDLineItem}_${IDMaterial}` then
	 * by SampleType; the SampleType aggregate's Planned/Required are the largest of its PLIMTs' fresh Computed counts;
	 * Bid/Installed come from the LINE ITEM (ContractQuantity / InstalledQuantity), as the server does.
	 */
	_buildSamplePlan(pBundle)
	{
		const tmpIdx = this._indexBundle(pBundle);
		const tmpSections = [];
		(pBundle.LineItems || []).forEach((pLineItem) =>
		{
			const tmpMLIJs = tmpIdx.mlijByLineItem[pLineItem.IDLineItem] || [];
			const tmpBid = parseFloat(pLineItem.ContractQuantity) || 0;
			const tmpInstalled = parseFloat(pLineItem.InstalledQuantity) || 0;
			const tmpRows = [];
			tmpMLIJs.forEach((pMLIJ) =>
			{
				const tmpMaterial = tmpIdx.materialByID[pMLIJ.IDMaterial] || {};
				const tmpPLIMTs = tmpIdx.plimtByJoin[`${pMLIJ.IDLineItem}_${pMLIJ.IDMaterial}`] || [];
				if (tmpPLIMTs.length === 0) { return; }
				const tmpByType = this._groupBy(tmpPLIMTs, (pPLIMT) => pPLIMT.SampleType || '');
				Object.keys(tmpByType).forEach((pTypeKey) =>
				{
					const tmpGroup = tmpByType[pTypeKey];
					// One sample serves every test in the group; same rule as SampleTypeSummary.ts in api/headlight
					const tmpPlanned = Math.max(...tmpGroup.map((pPLIMT) => Number(pPLIMT.ComputedPlannedSampleCount) || 0));
					const tmpRequired = Math.max(...tmpGroup.map((pPLIMT) => Number(pPLIMT.ComputedRequiredSampleCount) || 0));
					tmpGroup.forEach((pPLIMT) =>
					{
						tmpRows.push(
							{
								IDLineItem: pLineItem.IDLineItem, IDMaterial: pMLIJ.IDMaterial,
								MaterialName: this._materialName(tmpMaterial, pMLIJ.IDMaterial),
								MaterialCode: this._materialCode(tmpMaterial),
								SampleType: pPLIMT.SampleType || pTypeKey,
								Rate: pPLIMT.Rate, Frequency: pPLIMT.Frequency,
								BidQuantity: tmpBid, InstalledQuantity: tmpInstalled,
								PlannedSampleCount: tmpPlanned, RequiredSampleCount: tmpRequired,
								Search: `${this._materialName(tmpMaterial, pMLIJ.IDMaterial)} ${this._materialCode(tmpMaterial)} ${pPLIMT.SampleType || pTypeKey} ${pMLIJ.Units || ''}`.toLowerCase(),
							});
					});
				});
			});
			if (tmpRows.length === 0) { return; }
			tmpSections.push(this._lineItemSection(pLineItem, [{ Banner: null, Rows: tmpRows }]));
		});
		return { Project: pBundle.Project || {}, Sections: tmpSections };
	}

	/**
	 * Samples preset — line-item folders → material sub-groups → one row per SampleType (ports getDataForSamplesDashboard).
	 * Rate/Frequency come from the SampleType's first PLIMT; Units/ConversionFactor/ApprovedSource/SpecialInstructions
	 * from the MaterialLineItemJoin. Material is a sub-banner, not a column.
	 */
	_buildSamples(pBundle)
	{
		const tmpIdx = this._indexBundle(pBundle);
		const tmpSections = [];
		(pBundle.LineItems || []).forEach((pLineItem) =>
		{
			const tmpMLIJs = tmpIdx.mlijByLineItem[pLineItem.IDLineItem] || [];
			const tmpGroups = [];
			tmpMLIJs.forEach((pMLIJ) =>
			{
				const tmpMaterial = tmpIdx.materialByID[pMLIJ.IDMaterial] || {};
				const tmpPLIMTs = tmpIdx.plimtByJoin[`${pMLIJ.IDLineItem}_${pMLIJ.IDMaterial}`] || [];
				if (tmpPLIMTs.length === 0) { return; }
				const tmpMatName = this._materialName(tmpMaterial, pMLIJ.IDMaterial);
				const tmpMatCode = this._materialCode(tmpMaterial);
				const tmpByType = this._groupBy(tmpPLIMTs, (pPLIMT) => pPLIMT.SampleType || '');
				const tmpRows = Object.keys(tmpByType).map((pTypeKey) =>
				{
					const tmpFirst = tmpByType[pTypeKey][0] || {};
					return {
						IDLineItem: pLineItem.IDLineItem, IDMaterial: pMLIJ.IDMaterial,
						MaterialName: tmpMatName, MaterialCode: tmpMatCode,
						Units: pMLIJ.Units, SampleType: pTypeKey,
						Rate: tmpFirst.Rate, Frequency: tmpFirst.Frequency, ConversionFactor: pMLIJ.ConversionFactor,
						ApprovedSourceRequired: (Number(pMLIJ.ApprovedSourceRequired) === 1),
						SpecialInstructions: pMLIJ.SpecialInstructions,
						Search: `${this._lineItemName(pLineItem)} ${pLineItem.ItemCode || ''} ${tmpMatName} ${tmpMatCode} ${pTypeKey} ${pMLIJ.Units || ''}`.toLowerCase(),
					};
				});
				if (tmpRows.length === 0) { return; }
				tmpGroups.push({ Banner: { Name: tmpMatName, Code: tmpMatCode }, Rows: tmpRows });
			});
			if (tmpGroups.length === 0) { return; }
			tmpSections.push(this._lineItemSection(pLineItem, tmpGroups));
		});
		return { Project: pBundle.Project || {}, Sections: tmpSections };
	}

	/**
	 * Failing Samples preset — a FLAT list of every actual Sample with Status FAIL (ports getFailingSamples). One row per
	 * failing sample: Sample name (QR code + name), Units (SampledQuantityUnits), Type (MaterialType || RepresentedQuantityUnits),
	 * Disposition (the sample's Notes). Needs the Samples + SampleLineItemJoins parts.
	 */
	_buildFailingSamples(pBundle)
	{
		const tmpIdx = this._indexBundle(pBundle);
		const tmpLineItemByID = {};
		(pBundle.LineItems || []).forEach((pLineItem) => { tmpLineItemByID[pLineItem.IDLineItem] = pLineItem; });
		const tmpLineItemsBySample = {};
		(pBundle.SampleLineItemJoins || []).forEach((pSLIJ) => { (tmpLineItemsBySample[pSLIJ.IDSample] = tmpLineItemsBySample[pSLIJ.IDSample] || []).push(pSLIJ.IDLineItem); });

		const tmpFailing = (pBundle.Samples || []).filter((pSample) => pSample.Status === 'FAIL' && (Number(pSample.Deleted) !== 1));
		const tmpRows = tmpFailing.map((pSample) =>
		{
			const tmpMaterial = tmpIdx.materialByID[pSample.IDMaterial] || {};
			const tmpLineItemNames = (tmpLineItemsBySample[pSample.IDSample] || []).map((pID) => tmpLineItemByID[pID] ? this._lineItemName(tmpLineItemByID[pID]) : '').filter((pName) => !!pName);
			return {
				IDSample: pSample.IDSample,
				QRCode: pSample.QRCode || '',
				Name: pSample.Name || '',
				Units: pSample.SampledQuantityUnits || pSample.Units || '',
				Type: pSample.MaterialType || pSample.RepresentedQuantityUnits || '',
				Disposition: pSample.Notes || '',
				MaterialName: this._materialName(tmpMaterial, pSample.IDMaterial),
				MaterialCode: this._materialCode(tmpMaterial),
				LineItems: tmpLineItemNames,
				Search: `${pSample.QRCode || ''} ${pSample.Name || ''} ${pSample.MaterialType || ''} ${pSample.Notes || ''} ${this._materialName(tmpMaterial, pSample.IDMaterial)} ${tmpLineItemNames.join(' ')}`.toLowerCase(),
			};
		});
		// Flat: a single bannerless section holding one bannerless group.
		return { Project: pBundle.Project || {}, Sections: tmpRows.length ? [{ Banner: null, Search: '', Groups: [{ Banner: null, Rows: tmpRows }] }] : [] };
	}

	/**
	 * Custom preset — a configurable view. Builds one flat row per (LineItem × Material × SampleType) carrying EVERY
	 * catalog field, then regroups by `_customGroup` (None / LineItem / LineItemMaterial / Material). The column chooser
	 * (`_customColumns`) decides which fields render. Created/Passing/Failing come from the SampleType's first PLIMT's
	 * fresh Computed counts (lean), consistent with Planned/Required.
	 */
	_buildCustom(pBundle)
	{
		const tmpIdx = this._indexBundle(pBundle);
		// Sample counts per (IDLineItem, IDMaterial, SampleType) from ACTUAL sample records — the PLIMT's stored
		// Created/Passing/Failing counts are stale, so (like the full bundle) we count real samples: a line item's
		// samples (via SampleLineItemJoins) filtered to the material, grouped by the sample's MaterialType.
		const tmpCounts = this._sampleCountIndex(pBundle);
		const tmpFlat = [];
		(pBundle.LineItems || []).forEach((pLineItem) =>
		{
			const tmpMLIJs = tmpIdx.mlijByLineItem[pLineItem.IDLineItem] || [];
			const tmpBid = parseFloat(pLineItem.ContractQuantity) || 0;
			const tmpInstalled = parseFloat(pLineItem.InstalledQuantity) || 0;
			tmpMLIJs.forEach((pMLIJ) =>
			{
				const tmpMaterial = tmpIdx.materialByID[pMLIJ.IDMaterial] || {};
				const tmpPLIMTs = tmpIdx.plimtByJoin[`${pMLIJ.IDLineItem}_${pMLIJ.IDMaterial}`] || [];
				if (tmpPLIMTs.length === 0) { return; }
				const tmpMatName = this._materialName(tmpMaterial, pMLIJ.IDMaterial);
				const tmpMatCode = this._materialCode(tmpMaterial);
				const tmpByType = this._groupBy(tmpPLIMTs, (pPLIMT) => pPLIMT.SampleType || '');
				Object.keys(tmpByType).forEach((pTypeKey) =>
				{
					const tmpFirst = tmpByType[pTypeKey][0] || {};
					const tmpCount = tmpCounts[`${pLineItem.IDLineItem}|${pMLIJ.IDMaterial}|${pTypeKey}`] || { created: 0, passing: 0, failing: 0 };
					tmpFlat.push(
						{
							IDLineItem: pLineItem.IDLineItem, IDMaterial: pMLIJ.IDMaterial,
							LineItem: this._lineItemName(pLineItem), ItemCode: pLineItem.ItemCode || '',
							MaterialName: tmpMatName, MaterialCode: tmpMatCode, Material: tmpMatName,
							Units: pMLIJ.Units, SampleType: pTypeKey,
							Rate: tmpFirst.Rate, Frequency: tmpFirst.Frequency, ConversionFactor: pMLIJ.ConversionFactor,
							BidQuantity: tmpBid, InstalledQuantity: tmpInstalled,
							PlannedSampleCount: tmpFirst.ComputedPlannedSampleCount, RequiredSampleCount: tmpFirst.ComputedRequiredSampleCount,
							CreatedSamples: tmpCount.created, PassingSamples: tmpCount.passing, FailingSamples: tmpCount.failing,
							ApprovedSourceRequired: (Number(pMLIJ.ApprovedSourceRequired) === 1),
							SpecialInstructions: pMLIJ.SpecialInstructions,
							Search: `${this._lineItemName(pLineItem)} ${pLineItem.ItemCode || ''} ${tmpMatName} ${tmpMatCode} ${pTypeKey} ${pMLIJ.Units || ''}`.toLowerCase(),
						});
				});
			});
		});
		return { Project: pBundle.Project || {}, Sections: this._groupCustomRows(tmpFlat) };
	}

	/** Assemble Custom's flat rows into the uniform Section→Group model per the active grouping (bundle order preserved). */
	_groupCustomRows(pFlat)
	{
		if (pFlat.length === 0) { return []; }
		if (this._customGroup === 'None') { return [{ Banner: null, Search: '', Groups: [{ Banner: null, Rows: pFlat }] }]; }
		if (this._customGroup === 'Material')
		{
			const tmpByMat = this._groupOrdered(pFlat, (pRow) => pRow.IDMaterial);
			const tmpGroups = tmpByMat.map((pEntry) => ({ Banner: { Name: pEntry.Items[0].MaterialName, Code: pEntry.Items[0].MaterialCode }, Rows: pEntry.Items }));
			return [{ Banner: null, Search: '', Groups: tmpGroups }];
		}
		// LineItem or LineItemMaterial → a section per line item
		const tmpByLI = this._groupOrdered(pFlat, (pRow) => pRow.IDLineItem);
		return tmpByLI.map((pEntry) =>
		{
			const tmpRows = pEntry.Items;
			const tmpFirst = tmpRows[0];
			let tmpGroups;
			if (this._customGroup === 'LineItemMaterial')
			{
				tmpGroups = this._groupOrdered(tmpRows, (pRow) => pRow.IDMaterial).map((pMat) => ({ Banner: { Name: pMat.Items[0].MaterialName, Code: pMat.Items[0].MaterialCode }, Rows: pMat.Items }));
			}
			else { tmpGroups = [{ Banner: null, Rows: tmpRows }]; }
			return { Banner: { Name: tmpFirst.LineItem, Code: tmpFirst.ItemCode }, Search: `${tmpFirst.LineItem} ${tmpFirst.ItemCode}`.toLowerCase(), Groups: tmpGroups };
		});
	}

	/**
	 * Count real samples per (IDLineItem, IDMaterial, MaterialType) → { created, passing, failing }, mirroring how the
	 * full SamplePlanBundle derives its SampleType counts: a line item's samples (joined via SampleLineItemJoins) are
	 * matched to the material, then grouped by the sample's MaterialType (which equals the SampleType key). Requires the
	 * Samples + SampleLineItemJoins parts; returns an empty index (all zero) when they are absent.
	 */
	_sampleCountIndex(pBundle)
	{
		const tmpSampleByID = {};
		(pBundle.Samples || []).forEach((pSample) => { if (Number(pSample.Deleted) !== 1) { tmpSampleByID[pSample.IDSample] = pSample; } });
		const tmpIndex = {};
		(pBundle.SampleLineItemJoins || []).forEach((pSLIJ) =>
		{
			const tmpSample = tmpSampleByID[pSLIJ.IDSample];
			if (!tmpSample) { return; }
			const tmpKey = `${pSLIJ.IDLineItem}|${tmpSample.IDMaterial}|${tmpSample.MaterialType || ''}`;
			const tmpEntry = tmpIndex[tmpKey] || (tmpIndex[tmpKey] = { created: 0, passing: 0, failing: 0 });
			tmpEntry.created += 1;
			if (tmpSample.Status === 'PASS') { tmpEntry.passing += 1; }
			else if (tmpSample.Status === 'FAIL') { tmpEntry.failing += 1; }
		});
		return tmpIndex;
	}

	/** group-by that preserves first-seen key order (unlike a plain object, whose integer keys reorder numerically). */
	_groupOrdered(pList, pKeyFn)
	{
		const tmpOrder = []; const tmpMap = {};
		(pList || []).forEach((pItem) => { const tmpKey = pKeyFn(pItem); if (!tmpMap[tmpKey]) { tmpMap[tmpKey] = []; tmpOrder.push(tmpKey); } tmpMap[tmpKey].push(pItem); });
		return tmpOrder.map((pKey) => ({ Key: pKey, Items: tmpMap[pKey] }));
	}

	_lineItemSection(pLineItem, pGroups)
	{
		return {
			Banner: { Name: this._lineItemName(pLineItem), Code: pLineItem.ItemCode || pLineItem.ItemNumber || '' },
			Search: `${pLineItem.Name || ''} ${pLineItem.ItemCode || ''}`.toLowerCase(),
			Groups: pGroups,
		};
	}

	_groupBy(pList, pKeyFn) { const tmpOut = {}; (pList || []).forEach((pItem) => { const tmpKey = pKeyFn(pItem); (tmpOut[tmpKey] = tmpOut[tmpKey] || []).push(pItem); }); return tmpOut; }

	// --- Interactions (called from inline handlers) ---

	/** Switch the active preset tab. Lazily augments the fetch with the Samples parts the first time a sample-record
	 *  preset (Failing Samples) is opened; otherwise repaints from the cached bundle. */
	async setPreset(pKey)
	{
		if (this._preset === pKey) { return; }
		const tmpPreset = this.presets.find((pPreset) => pPreset.Key === pKey);
		if (!tmpPreset) { return; }
		this._preset = pKey;
		this._search = '';
		this._sortBy = tmpPreset.DefaultSort || 'Material';
		this._sortDesc = false;

		const tmpState = this.state;
		if (tmpPreset.NeedsSamples && !this._haveSamples && this._idProject)
		{
			tmpState.Loading = true; tmpState.Error = ''; this.render();
			try
			{
				this._rawBundle = (await this._postJSON(`SamplePlanBundle/Parts/By/IDProject/${this._idProject}`, { Parts: this._partsFor(tmpPreset) })) || this._rawBundle;
				this._haveSamples = true;
			}
			catch (pError) { tmpState.Error = `Could not load samples: ${pError && pError.message ? pError.message : pError}`; }
			tmpState.Loading = false;
		}
		if (this._rawBundle) { this._rebuild(); }
		this.render();
	}

	setSearch(pValue) { this._search = String(pValue == null ? '' : pValue).toLowerCase(); this._paintTable(); }

	/** Print / save-as-PDF the active tab. Print CSS (`@media print`) hides the app chrome + this dashboard's own
	 *  toolbar/tabs/action buttons, leaving just the titled grid. */
	print()
	{
		try
		{
			const tmpTitle = document.title;
			const tmpName = `${this.state.ProjectName || 'Project'} - ${this.activePreset.Label}`.replace(/[^\w .-]+/g, ' ').trim();
			document.title = tmpName || tmpTitle;
			const fRestore = () => { document.title = tmpTitle; window.removeEventListener('afterprint', fRestore); };
			window.addEventListener('afterprint', fRestore);
			window.print();
		}
		catch (pError) { try { window.print(); } catch (pInner) { /* best-effort */ } }
	}

	setSort(pColumn)
	{
		if (this._sortBy === pColumn) { this._sortDesc = !this._sortDesc; }
		else { this._sortBy = pColumn; this._sortDesc = false; }
		this._paintTable();
	}

	/** Custom: change the grouping (regroups the rows) — repaints the shell so the active group button + table update. */
	setCustomGroup(pKey)
	{
		if (this._customGroup === pKey) { return; }
		this._customGroup = pKey;
		if (this._rawBundle) { this._rebuild(); }
		this.render();
	}

	/** Custom: toggle a column in the chooser — only the table (and the count badge) repaint, so the panel stays open. */
	toggleCustomColumn(pKey)
	{
		const tmpIndex = this._customColumns.indexOf(pKey);
		if (tmpIndex >= 0) { this._customColumns.splice(tmpIndex, 1); }
		else { this._customColumns.push(pKey); }
		if (this._customColumns.length === 0) { this._customColumns.push(pKey); }   // never leave zero columns
		this._paintTable();
		try { const tmpEl = document.querySelector(`${this._mount || '#SamplePlanDashboard-Host'} .spd-custom-count`); if (tmpEl) { tmpEl.textContent = String(this._customColumns.length); } } catch (pError) { /* best-effort */ }
	}

	// --- Failing-sample disposition editing (inline; writes Sample.Notes) ---

	editDisposition(pIDSample)
	{
		this._editingSample = Number(pIDSample) || 0;
		this._paintTable();
		// Focus the freshly-painted textarea and drop the cursor at the end.
		try { const tmpEl = document.getElementById(`spd-dispo-input-${Number(pIDSample)}`); if (tmpEl) { tmpEl.focus(); tmpEl.selectionStart = tmpEl.selectionEnd = tmpEl.value.length; } } catch (pError) { /* best-effort */ }
	}

	cancelDisposition() { this._editingSample = 0; this._paintTable(); }

	async saveDisposition(pIDSample)
	{
		const tmpID = Number(pIDSample) || 0;
		if (!tmpID) { return; }
		let tmpValue = '';
		try { const tmpEl = document.getElementById(`spd-dispo-input-${tmpID}`); tmpValue = tmpEl ? String(tmpEl.value || '') : ''; } catch (pError) { /* best-effort */ }
		await this._commitDisposition(tmpID, tmpValue);
	}

	async deleteDisposition(pIDSample)
	{
		const tmpID = Number(pIDSample) || 0;
		if (!tmpID) { return; }
		await this._commitDisposition(tmpID, '');
	}

	/** PUT the sample's Notes, then reflect it in the cached bundle + model so repaints show the saved value. */
	async _commitDisposition(pIDSample, pNotes)
	{
		this._editingSample = 0;
		this._savingSample = pIDSample;
		this._paintTable();
		try
		{
			await this._putJSON('Sample', { IDSample: pIDSample, Notes: pNotes });
			const tmpSample = (this._rawBundle && this._rawBundle.Samples || []).find((pSample) => pSample.IDSample === pIDSample);
			if (tmpSample) { tmpSample.Notes = pNotes; }
			const tmpRow = this._findFailingRow(pIDSample);
			if (tmpRow) { tmpRow.Disposition = pNotes; tmpRow.Search = `${tmpRow.QRCode || ''} ${tmpRow.Name || ''} ${tmpRow.Type || ''} ${pNotes || ''} ${tmpRow.MaterialName || ''} ${(tmpRow.LineItems || []).join(' ')}`.toLowerCase(); }
			this._toast('Disposition saved', 'success');
		}
		catch (pError) { this._toast(`Could not save disposition: ${pError && pError.message ? pError.message : pError}`, 'error'); }
		this._savingSample = 0;
		this._paintTable();
	}

	_findFailingRow(pIDSample)
	{
		const tmpSections = (this._model && this._model.Sections) || [];
		for (const tmpSection of tmpSections) { for (const tmpGroup of tmpSection.Groups) { const tmpFound = tmpGroup.Rows.find((pRow) => pRow.IDSample === pIDSample); if (tmpFound) { return tmpFound; } } }
		return null;
	}

	/** Fire a toast via the shared modal provider when available (guards the duration-undefined clobber). */
	_toast(pMessage, pKind)
	{
		try
		{
			const tmpModal = this.pict.providers && (this.pict.providers['Pict-Section-Modal'] || this.pict.providers['PictSectionModal']);
			if (tmpModal && (typeof tmpModal.toast === 'function')) { tmpModal.toast({ Content: pMessage, Type: (pKind === 'error') ? 'danger' : 'success', Duration: 3000 }); }
		}
		catch (pError) { /* best-effort */ }
	}

	/** The "New Sample" action for a row (only when ShowAddSample is on). Navigates to the bespoke Sample create,
	 *  seeding the project/line-item/material — the config-app equivalent of the legacy postMessage-to-host. */
	addSampleForRow(pIDLineItem, pIDMaterial, pType)
	{
		if (!this.showAddSample) { return; }
		try { window.location.hash = `#/PSRS/Sample/Create?IDProject=${Number(this._idProject)}&IDLineItem=${Number(pIDLineItem)}&IDMaterial=${Number(pIDMaterial)}&Type=${encodeURIComponent(String(pType || ''))}`; }
		catch (pError) { /* best-effort */ }
	}

	// --- Filtering + sorting (view-time) ---

	/** Compute the rendered view: sections → groups → filtered + sorted rows, with empties dropped. */
	_computeView()
	{
		const tmpQuery = (this._search || '').trim();
		const tmpSections = (this._model && this._model.Sections) || [];
		const tmpOut = [];
		tmpSections.forEach((pSection) =>
		{
			const tmpSectionMatches = pSection.Banner ? (pSection.Search.indexOf(tmpQuery) >= 0) : false;
			const tmpGroups = [];
			pSection.Groups.forEach((pGroup) =>
			{
				let tmpRows = pGroup.Rows;
				if (tmpQuery && !tmpSectionMatches) { tmpRows = tmpRows.filter((pRow) => pRow.Search.indexOf(tmpQuery) >= 0); }
				tmpRows = this._sortRows(tmpRows);
				if (tmpRows.length > 0) { tmpGroups.push({ Banner: pGroup.Banner, Rows: tmpRows }); }
			});
			if (tmpGroups.length > 0) { tmpOut.push({ Banner: pSection.Banner, Groups: tmpGroups, RowCount: tmpGroups.reduce((pSum, pGroup) => pSum + pGroup.Rows.length, 0) }); }
		});
		return tmpOut;
	}

	_sortRows(pRows)
	{
		const tmpKey = this._sortBy;
		const tmpDir = this._sortDesc ? -1 : 1;
		const fVal = (pRow) => (tmpKey === 'Material') ? String(pRow.MaterialName || '') : pRow[tmpKey];
		return pRows.slice().sort((pA, pB) =>
		{
			const tmpA = fVal(pA); const tmpB = fVal(pB);
			const tmpNumA = Number(tmpA); const tmpNumB = Number(tmpB);
			if (Number.isFinite(tmpNumA) && Number.isFinite(tmpNumB) && (tmpKey !== 'Material')) { return (tmpNumA - tmpNumB) * tmpDir; }
			return String(tmpA == null ? '' : tmpA).localeCompare(String(tmpB == null ? '' : tmpB)) * tmpDir;
		});
	}

	// --- Columns (per preset) ---

	_columns()
	{
		const tmpCols = [];
		if (this._preset === 'Custom')
		{
			const tmpHasLineItem = (this._customGroup === 'LineItem' || this._customGroup === 'LineItemMaterial');
			const tmpHasMaterial = (this._customGroup === 'Material' || this._customGroup === 'LineItemMaterial');
			const tmpChosen = SamplePlanDashboardProvider.CUSTOM_COLUMN_CATALOG
				.filter((pCol) => this._customColumns.indexOf(pCol.Key) >= 0)
				.filter((pCol) => !(tmpHasLineItem && pCol.Banner === 'lineitem'))
				.filter((pCol) => !(tmpHasMaterial && pCol.Banner === 'material'))
				.map((pCol) => Object.assign({}, pCol));
			// Give exactly one column the flex/wrap role so the grid fills the width without a scroll.
			const tmpFlex = tmpChosen.find((pCol) => pCol.Key === 'Material') || tmpChosen.find((pCol) => pCol.Key === 'SpecialInstructions') || tmpChosen.find((pCol) => pCol.Align === 'left');
			if (tmpFlex) { tmpFlex.Flex = true; if (tmpFlex.Text) { tmpFlex.Wrap = true; } }
			return tmpChosen.length ? tmpChosen : [{ Key: 'SampleType', Label: 'Type', Align: 'center' }];
		}
		if (this._preset === 'FailingSamples')
		{
			tmpCols.push({ Key: 'Name', Label: 'Sample', Align: 'left', Flex: true });
			tmpCols.push({ Key: 'Units', Label: 'Units', Align: 'left' });
			tmpCols.push({ Key: 'Type', Label: 'Type', Align: 'center' });
			tmpCols.push({ Key: 'Disposition', Label: 'Disposition', Align: 'left', Flex: true, Wrap: true });
			tmpCols.push({ Key: 'DispositionAction', Label: '', Align: 'left', NoSort: true, NoPrint: true });
			return tmpCols;
		}
		if (this._preset === 'Samples')
		{
			if (this.showAddSample) { tmpCols.push({ Key: 'Action', Label: '', Align: 'center', NoSort: true, NoPrint: true }); }
			tmpCols.push({ Key: 'Units', Label: 'Units', Align: 'left' });
			tmpCols.push({ Key: 'SampleType', Label: 'Type', Align: 'center' });
			tmpCols.push({ Key: 'Rate', Label: 'Rate', Align: 'right', Clean: true });
			tmpCols.push({ Key: 'Frequency', Label: 'Frequency', Align: 'right', Clean: true });
			tmpCols.push({ Key: 'ConversionFactor', Label: 'Conversion Factor', Align: 'right', Clean: true });
			tmpCols.push({ Key: 'ApprovedSourceRequired', Label: 'Approved Source', Align: 'center', Bool: true, NoSort: true });
			tmpCols.push({ Key: 'SpecialInstructions', Label: 'Special Instructions', Align: 'left', Flex: true, Wrap: true, Text: true });
			return tmpCols;
		}
		// SamplePlan
		if (this.showAddSample) { tmpCols.push({ Key: 'Action', Label: '', Align: 'center', NoSort: true, NoPrint: true }); }
		tmpCols.push({ Key: 'Material', Label: 'Material', Align: 'left', Flex: true });
		tmpCols.push({ Key: 'SampleType', Label: 'Type', Align: 'center' });
		tmpCols.push({ Key: 'Rate', Label: 'Rate', Align: 'right', Clean: true });
		tmpCols.push({ Key: 'Frequency', Label: 'Frequency', Align: 'right', Clean: true });
		tmpCols.push({ Key: 'BidQuantity', Label: 'Bid Qty.', Align: 'right', Clean: true });
		tmpCols.push({ Key: 'InstalledQuantity', Label: 'Installed Qty.', Align: 'right', Number: true });
		tmpCols.push({ Key: 'PlannedSampleCount', Label: 'Planned Samples', Align: 'right', Number: true });
		tmpCols.push({ Key: 'RequiredSampleCount', Label: 'Req. Samples', Align: 'right', Number: true });
		return tmpCols;
	}

	// --- Render (host-div paint) ---

	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpSel = this._mount || '#SamplePlanDashboard-Host';
		if (!tmpCA || tmpCA.getElement(tmpSel).length === 0) { return; }
		tmpCA.assignContent(tmpSel, this._shellHTML());
		this._paintTable();
	}
	// Full screen: overlay the dashboard across the whole browser view (above the app nav). State is a provider field so it
	// survives the shell rebuild; `.spd-fs` composites on its own layer (CSS transform) so it paints immediately. Standalone
	// only (embed offers a pop-out to this standalone route instead). Navigating away replaces the host → overlay torn down.
	toggleFullscreen() { if (this.isEmbedded) { return; } this._fullscreen = !this._fullscreen; this.render(); }

	_shellHTML()
	{
		const tmpState = this.state;
		const tmpCount = tmpState.RowCount || 0;
		const tmpTabs = this.presets.map((pPreset) =>
		{
			const tmpActive = (pPreset.Key === this._preset) ? ' spd-tab-active' : '';
			return `<button class="spd-tab${tmpActive}" onclick="_Pict.providers.${this.providerName}.setPreset('${pPreset.Key}')"><i class="fas ${pPreset.Icon}"></i> ${this._esc(pPreset.Label)}</button>`;
		}).join('');
		return /*html*/`
			<div class="spd${this._fullscreen ? ' spd-fullscreen' : ''}">
				<div class="spd-head">
					<div class="spd-title"><i class="fas fa-clipboard-check"></i> Material LineItem Samples</div>
					<div class="spd-headright"><span class="spd-count" id="spd-count">${tmpCount}</span></div>
				</div>
				<div class="spd-tabs">${tmpTabs}</div>
				${this._preset === 'Custom' ? this._customControlsHTML() : ''}
				<div class="spd-toolbar">
					<span class="spd-search"><i class="fas fa-search spd-search-ic"></i><input class="spd-search-input" type="search" placeholder="${this._esc(this.activePreset.SearchPlaceholder || 'Filter…')}" value="${this._esc(this._search)}" oninput="_Pict.providers.${this.providerName}.setSearch(this.value)" /></span>
					<button class="spd-print" title="Print / save as PDF" onclick="_Pict.providers.${this.providerName}.print()"><i class="fas fa-print"></i> Print</button>
					${this.isEmbedded
						? `<a class="spd-popout" title="Open this dashboard full screen" href="#/SamplePlanDashboard/${Number(this._idProject)}"><i class="fas fa-up-right-and-down-left-from-center"></i> Full screen</a>`
						: `<button class="spd-fullscreen-btn${this._fullscreen ? ' spd-fullscreen-btn-on' : ''}" title="${this._fullscreen ? 'Exit full screen (Esc)' : 'Full screen'}" onclick="_Pict.providers.${this.providerName}.toggleFullscreen()"><i class="fas ${this._fullscreen ? 'fa-compress' : 'fa-expand'}"></i> ${this._fullscreen ? 'Exit full screen' : 'Full screen'}</button>`}
				</div>
				<div class="spd-printhead" id="spd-printhead">${this._esc(this.state.ProjectName || '')} — ${this._esc(this.activePreset.Label)}</div>
				<div id="spd-table"></div>
			</div>`;
	}

	/** The Custom preset's controls: a grouping selector + a collapsible column chooser. */
	_customControlsHTML()
	{
		const tmpProv = `_Pict.providers.${this.providerName}`;
		const tmpGroupBtns = SamplePlanDashboardProvider.CUSTOM_GROUPS.map((pGroup) =>
		{
			const tmpActive = (pGroup.Key === this._customGroup) ? ' spd-cg-active' : '';
			return `<button class="spd-cg${tmpActive}" onclick="${tmpProv}.setCustomGroup('${pGroup.Key}')">${this._esc(pGroup.Label)}</button>`;
		}).join('');
		const tmpChecks = SamplePlanDashboardProvider.CUSTOM_COLUMN_CATALOG.map((pCol) =>
		{
			const tmpChecked = (this._customColumns.indexOf(pCol.Key) >= 0) ? ' checked' : '';
			return `<label class="spd-colpick"><input type="checkbox"${tmpChecked} onchange="${tmpProv}.toggleCustomColumn('${pCol.Key}')" /> ${this._esc(pCol.Label)}</label>`;
		}).join('');
		return /*html*/`
			<div class="spd-custom">
				<div class="spd-custom-row"><span class="spd-custom-label">Group by</span><div class="spd-cg-bar">${tmpGroupBtns}</div></div>
				<details class="spd-custom-cols">
					<summary><i class="fas fa-table-columns"></i> Columns <span class="spd-custom-count">${this._customColumns.length}</span></summary>
					<div class="spd-colgrid">${tmpChecks}</div>
				</details>
			</div>`;
	}

	/** Repaint ONLY the table region (keeps the search input + its focus intact across search/sort). */
	_paintTable()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpSel = this._mount || '#SamplePlanDashboard-Host';
		if (!tmpCA || tmpCA.getElement(`${tmpSel} #spd-table`).length === 0) { return; }
		tmpCA.assignContent(`${tmpSel} #spd-table`, this._tableHTML());
		if (tmpCA.getElement(`${tmpSel} #spd-count`).length > 0) { tmpCA.assignContent(`${tmpSel} #spd-count`, String(this._visibleRowCount())); }
	}

	_visibleRowCount() { return this._computeView().reduce((pSum, pSection) => pSum + pSection.RowCount, 0); }

	_tableHTML()
	{
		const tmpState = this.state;
		if (tmpState.Loading) { return `<div class="spd-empty"><span class="spd-spinner"></span> Loading…</div>`; }
		if (tmpState.Error) { return `<div class="spd-empty spd-error">${this._esc(tmpState.Error)}</div>`; }
		if (!tmpState.Loaded) { return `<div class="spd-empty">Choose a project to view its sample plan.</div>`; }

		const tmpView = this._computeView();
		if (tmpView.length === 0)
		{
			if (this._search.trim())
			{
				return `<div class="spd-empty">No results matching <span class="spd-term">${this._esc(this._search)}</span> <button class="spd-clear" title="Clear filter" onclick="_Pict.providers.${this.providerName}.setSearch('')"><i class="fas fa-times"></i></button></div>`;
			}
			return `<div class="spd-empty">${this._esc(this.activePreset.EmptyText || 'Nothing to display for this project.')}</div>`;
		}
		// ONE full-width table: line-item / material banners are full-span rows interleaved with their data rows, so every
		// column aligns across the whole grid and there is at most a single (page-level) scroll — never a per-section box.
		const tmpCols = this._columns();
		const tmpHead = tmpCols.map((pCol) => this._headCellHTML(pCol)).join('');
		const tmpBody = tmpView.map((pSection) => this._sectionHTML(pSection, tmpCols)).join('');
		return /*html*/`
			<div class="spd-tablewrap">
				<table class="spd-grid">
					<thead><tr>${tmpHead}</tr></thead>
					<tbody>${tmpBody}</tbody>
				</table>
			</div>`;
	}

	/** A section = optional line-item banner + its groups (each an optional material sub-banner + rows). */
	_sectionHTML(pSection, pCols)
	{
		let tmpHTML = '';
		if (pSection.Banner)
		{
			const tmpCode = pSection.Banner.Code ? `<span class="spd-li-code">${this._esc(pSection.Banner.Code)}</span>` : '';
			tmpHTML += `<tr class="spd-li-row"><td class="spd-li-cell" colspan="${pCols.length}"><div class="spd-li-inner"><span class="spd-li-ic"><i class="fas fa-folder"></i></span><span class="spd-li-name">${this._esc(pSection.Banner.Name)}</span>${tmpCode}<span class="spd-li-badge">${pSection.RowCount}</span></div></td></tr>`;
		}
		pSection.Groups.forEach((pGroup) =>
		{
			if (pGroup.Banner)
			{
				const tmpCode = pGroup.Banner.Code ? `<span class="spd-mat-code">${this._esc(pGroup.Banner.Code)}</span>` : '';
				tmpHTML += `<tr class="spd-mat-row"><td class="spd-mat-cell" colspan="${pCols.length}"><div class="spd-mat-inner"><span class="spd-mat-ic"><i class="fas fa-layer-group"></i></span><span class="spd-mat-name">${this._esc(pGroup.Banner.Name)}</span>${tmpCode}<span class="spd-mat-badge">${pGroup.Rows.length}</span></div></td></tr>`;
			}
			tmpHTML += pGroup.Rows.map((pRow) => this._rowHTML(pRow, pCols)).join('');
		});
		return tmpHTML;
	}

	_headCellHTML(pCol)
	{
		const tmpCls = `spd-th spd-${pCol.Align}${pCol.Flex ? ' spd-flex' : ''}${pCol.NoPrint ? ' spd-noprint' : ''}`;
		const tmpArrow = (this._sortBy === pCol.Key) ? ` <i class="fas fa-caret-${this._sortDesc ? 'down' : 'up'}"></i>` : '';
		if (pCol.NoSort) { return `<th class="${tmpCls}">${pCol.Label}</th>`; }
		return `<th class="${tmpCls} spd-sortable" onclick="_Pict.providers.${this.providerName}.setSort('${pCol.Key}')">${pCol.Label}${tmpArrow}</th>`;
	}

	_rowHTML(pRow, pCols)
	{
		return `<tr class="spd-row">${pCols.map((pCol) => this._cellHTML(pRow, pCol)).join('')}</tr>`;
	}

	_cellHTML(pRow, pCol)
	{
		const tmpCls = `spd-td spd-${pCol.Align}${pCol.Flex ? ' spd-flex' : ''}${pCol.Wrap ? ' spd-wrap' : ''}${pCol.NoPrint ? ' spd-noprint' : ''}`;
		if (pCol.Key === 'Action')
		{
			return `<td class="${tmpCls} spd-action spd-noprint"><button class="spd-newbtn" onclick="_Pict.providers.${this.providerName}.addSampleForRow(${Number(pRow.IDLineItem)}, ${Number(pRow.IDMaterial)}, '${this._esc(pRow.SampleType)}')"><i class="fas fa-plus"></i> New Sample</button></td>`;
		}
		if (pCol.Key === 'Material')
		{
			const tmpChip = pRow.MaterialCode ? `<span class="spd-matcode">${this._esc(pRow.MaterialCode)}</span>` : '';
			return `<td class="${tmpCls} spd-material"><span class="spd-matname">${this._esc(pRow.MaterialName)}</span>${tmpChip}</td>`;
		}
		if (pCol.Key === 'Name')   // Failing Samples: QR code + sample name (two lines)
		{
			const tmpQR = pRow.QRCode ? `<span class="spd-fs-qr">${this._esc(pRow.QRCode)}</span>` : '';
			const tmpName = pRow.Name ? `<span class="spd-fs-name">${this._esc(pRow.Name)}</span>` : '';
			return `<td class="${tmpCls} spd-fs-namecell">${tmpQR}${tmpName || (tmpQR ? '' : '<span class="spd-muted">—</span>')}</td>`;
		}
		if (pCol.Key === 'Disposition')
		{
			if (this._editingSample && (this._editingSample === pRow.IDSample))
			{
				return `<td class="${tmpCls}"><textarea id="spd-dispo-input-${Number(pRow.IDSample)}" class="spd-dispo-input" rows="2" placeholder="Enter a disposition for this failing sample…">${this._esc(pRow.Disposition || '')}</textarea></td>`;
			}
			const tmpNote = pRow.Disposition;
			if (tmpNote == null || tmpNote === '') { return `<td class="${tmpCls}"><span class="spd-dispo-empty">No disposition recorded</span></td>`; }
			return `<td class="${tmpCls}">${this._esc(tmpNote)}</td>`;
		}
		if (pCol.Key === 'DispositionAction')
		{
			const tmpID = Number(pRow.IDSample);
			const tmpProv = `_Pict.providers.${this.providerName}`;
			if (this._savingSample === pRow.IDSample) { return `<td class="${tmpCls} spd-dispo-actions spd-noprint"><span class="spd-spinner"></span></td>`; }
			if (this._editingSample === pRow.IDSample)
			{
				return `<td class="${tmpCls} spd-dispo-actions spd-noprint"><button class="spd-btn spd-btn-save" onclick="${tmpProv}.saveDisposition(${tmpID})">Save</button><button class="spd-btn spd-btn-ghost" onclick="${tmpProv}.cancelDisposition()">Cancel</button></td>`;
			}
			const tmpHasNote = !!(pRow.Disposition && String(pRow.Disposition).length);
			const tmpEdit = `<button class="spd-btn${tmpHasNote ? ' spd-btn-ghost' : ' spd-btn-warn'}" onclick="${tmpProv}.editDisposition(${tmpID})">${tmpHasNote ? 'Edit' : 'Add disposition'}</button>`;
			const tmpDel = tmpHasNote ? `<button class="spd-btn spd-btn-del" title="Clear disposition" onclick="${tmpProv}.deleteDisposition(${tmpID})"><i class="fas fa-trash"></i></button>` : '';
			return `<td class="${tmpCls} spd-dispo-actions spd-noprint">${tmpEdit}${tmpDel}</td>`;
		}
		if (pCol.Bool)
		{
			return pRow[pCol.Key]
				? `<td class="${tmpCls}"><i class="fas fa-check-square spd-ok"></i></td>`
				: `<td class="${tmpCls} spd-muted"><i class="far fa-square"></i></td>`;
		}
		if (pCol.Number)
		{
			const tmpVal = this._num(pRow[pCol.Key]);
			return `<td class="${tmpCls}${this._isZero(pRow[pCol.Key]) ? ' spd-muted' : ''}">${tmpVal === '' ? '—' : this._esc(tmpVal)}</td>`;
		}
		if (pCol.Clean)
		{
			// Numeric-ish rate/frequency/quantity — strip trailing zeros (1000.00000 → 1000), else N/A.
			const tmpN = parseFloat(pRow[pCol.Key]);
			if (Number.isFinite(tmpN)) { return `<td class="${tmpCls}${tmpN === 0 ? ' spd-muted' : ''}">${this._esc(String(tmpN))}</td>`; }
			return `<td class="${tmpCls} spd-muted">N/A</td>`;
		}
		const tmpRaw = pRow[pCol.Key];
		const tmpText = (tmpRaw == null || tmpRaw === '') ? 'N/A' : String(tmpRaw);
		return `<td class="${tmpCls}${(tmpRaw == null || tmpRaw === '') ? ' spd-muted' : ''}">${this._esc(tmpText)}</td>`;
	}

	// --- Standalone-screen project selection (front door) ---

	/** The router pushes the route's :IDProject here; the standalone view reads it in onAfterRender to open the dashboard. */
	setRouteProject(pIDProject) { this._routeIDProject = Number(pIDProject) || 0; }
	get routeProject() { return this._routeIDProject || 0; }

	/** Picker chose a project → navigate to the shareable per-project route (the router opens the dashboard). */
	selectProject(pIDProject)
	{
		const tmpID = Number(pIDProject) || 0;
		if (tmpID) { try { window.location.hash = `#/SamplePlanDashboard/${tmpID}`; } catch (pError) { this.openInto(tmpID, this._mount); } }
	}

	_projectDataProvider()
	{
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._getJSON(pURL),
			{
				Set: 'Projects/Lite', IDField: 'IDProject',
				ToOption: (pRow) => ({ Value: Number(pRow.IDProject), Text: pRow.Value || pRow.Name || pRow.Description || `Project ${pRow.IDProject}`, Record: pRow }),
			});
	}

	_projectResolveValue()
	{
		return (pValue) => { const tmpID = Number(pValue); if (!tmpID) { return Promise.resolve(null); } return this._getJSON(`Project/${tmpID}`).then((pRow) => ((pRow && pRow.IDProject) ? { Value: tmpID, Text: pRow.Name || pRow.Description || `#${tmpID}` } : { Value: tmpID, Text: `#${tmpID}` })).catch(() => ({ Value: tmpID, Text: `#${tmpID}` })); };
	}

	/** Mount the searchable project picker into the standalone view's host (idempotent per render). */
	mountProjectPicker(pHostSelector)
	{
		const tmpPicker = this.pict.providers && this.pict.providers['Pict-Section-Picker'];
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA) { return; }
		if (tmpCA.getElement(pHostSelector).length === 0) { return; }
		const tmpView = tmpPicker.createPicker(`${this.providerName}-ProjectPicker`,
			{
				DestinationAddress: pHostSelector,
				ValueAddress: `AppData.${this.providerName}.PickerProjectID`,
				Searchable: true, AllowClear: true, ClearLabel: '— pick a project —', Placeholder: 'Search projects…',
				DataProvider: this._projectDataProvider(), ResolveValue: this._projectResolveValue(),
				OnChange: (pValue) => this.selectProject(pValue),
			});
		if (tmpView && (typeof tmpView.render === 'function')) { tmpView.render(); }
		if (tmpView && (typeof tmpView.setValue === 'function')) { tmpView.setValue(this._idProject || ''); }
	}

	/** Re-render the standalone view (when embedded, the host repaints via its own onAfterRender). */
	_renderStandaloneView() { if (this.pict.views[this.providerName]) { this.pict.views[this.providerName].render(); } }
}

module.exports = SamplePlanDashboardProvider;

/** The minimal SamplePlanBundle parts every preset needs — a single lean streamed request (see loadAsync). */
SamplePlanDashboardProvider.BUNDLE_PARTS = ['Project', 'LineItems', 'MaterialLineItemJoins', 'Materials', 'ProjectLineItemMaterialTests'];

/** The preset tabs (order + icons mirror the legacy dashboard.json Presets, by Ordinal). */
SamplePlanDashboardProvider.PRESETS =
[
	{ Key: 'SamplePlan', Label: 'Sample Plan', Icon: 'fa-tasks', DefaultSort: 'Material', NeedsSamples: false, SearchPlaceholder: 'Filter by line item, material, type, or units…', EmptyText: 'No planned samples for this project.' },
	{ Key: 'Samples', Label: 'Samples', Icon: 'fa-vials', DefaultSort: 'SampleType', NeedsSamples: false, SearchPlaceholder: 'Filter by line item, material, type, or units…', EmptyText: 'No sample types for this project.' },
	{ Key: 'FailingSamples', Label: 'Failing Samples', Icon: 'fa-triangle-exclamation', DefaultSort: 'Name', NeedsSamples: true, SearchPlaceholder: 'Filter by sample, material, type, or disposition…', EmptyText: 'No failing samples for this project.' },
	{ Key: 'Custom', Label: 'Custom', Icon: 'fa-sliders', DefaultSort: 'Material', NeedsSamples: true, SearchPlaceholder: 'Filter by line item, material, type, or units…', EmptyText: 'No data for this project.' },
];

/** The Custom preset's full column catalog (order = render order); the chooser toggles membership in _customColumns. */
SamplePlanDashboardProvider.CUSTOM_COLUMN_CATALOG =
[
	{ Key: 'LineItem', Label: 'Line Item', Align: 'left', Banner: 'lineitem' },
	{ Key: 'ItemCode', Label: 'Pay Item Code', Align: 'left', Banner: 'lineitem' },
	{ Key: 'Material', Label: 'Material', Align: 'left', Banner: 'material' },
	{ Key: 'MaterialCode', Label: 'Material Code', Align: 'left', Banner: 'material' },
	{ Key: 'Units', Label: 'Units', Align: 'left' },
	{ Key: 'SampleType', Label: 'Type', Align: 'center' },
	{ Key: 'Rate', Label: 'Rate', Align: 'right', Clean: true },
	{ Key: 'Frequency', Label: 'Frequency', Align: 'right', Clean: true },
	{ Key: 'ConversionFactor', Label: 'Conversion Factor', Align: 'right', Clean: true },
	{ Key: 'BidQuantity', Label: 'Bid Qty.', Align: 'right', Clean: true },
	{ Key: 'InstalledQuantity', Label: 'Installed Qty.', Align: 'right', Number: true },
	{ Key: 'PlannedSampleCount', Label: 'Planned Samples', Align: 'right', Number: true },
	{ Key: 'RequiredSampleCount', Label: 'Req. Samples', Align: 'right', Number: true },
	{ Key: 'CreatedSamples', Label: 'Created Samples', Align: 'right', Number: true },
	{ Key: 'PassingSamples', Label: 'Passing Samples', Align: 'right', Number: true },
	{ Key: 'FailingSamples', Label: 'Failing Samples', Align: 'right', Number: true },
	{ Key: 'ApprovedSourceRequired', Label: 'Approved Source', Align: 'center', Bool: true, NoSort: true },
	{ Key: 'SpecialInstructions', Label: 'Special Instructions', Align: 'left', Text: true },
];

/** Custom preset — the columns shown before the user customizes (mirrors dashboard.json Presets.Custom None DefaultValues). */
SamplePlanDashboardProvider.CUSTOM_DEFAULT_COLUMNS =
	['Units', 'SampleType', 'Rate', 'Frequency', 'ConversionFactor', 'BidQuantity', 'InstalledQuantity', 'PlannedSampleCount', 'RequiredSampleCount', 'CreatedSamples', 'PassingSamples', 'FailingSamples', 'ApprovedSourceRequired', 'SpecialInstructions'];

/** Custom preset — the grouping options (mirror dashboard.json Presets.Custom Groups). */
SamplePlanDashboardProvider.CUSTOM_GROUPS =
[
	{ Key: 'None', Label: 'None' },
	{ Key: 'LineItem', Label: 'Line Item' },
	{ Key: 'LineItemMaterial', Label: 'Line Item + Material' },
	{ Key: 'Material', Label: 'Material' },
];

module.exports.default_configuration =
{
	ProviderIdentifier: 'SamplePlanDashboard',
};

const _CSS = /*css*/`
	/* Fill the container. Width comes from the host: the app-shell Dashboard Mode makes the standalone screen full
	   browser width; embedded in a workspace tab it fills the workspace content width. */
	.spd { width: 100%; }
	.spd-head { display: flex; align-items: center; justify-content: space-between; gap: 0.8rem; margin: 0 0 0.6rem; }
	.spd-title { font-size: 1.05rem; font-weight: 700; color: var(--theme-color-text-primary,#1f2733); display: inline-flex; align-items: center; gap: 0.5rem; }
	.spd-title i { color: var(--theme-color-brand-primary,#2e7d74); }
	.spd-count { display: inline-block; min-width: 1.6rem; text-align: center; font-size: 0.78rem; font-weight: 700; color: #fff; background: var(--theme-color-text-secondary,#45505f); border-radius: 999px; padding: 0.12rem 0.55rem; }

	/* Preset tab pills */
	.spd-tabs { display: flex; flex-wrap: wrap; gap: 0.35rem; margin: 0 0 0.85rem; border-bottom: 1px solid var(--theme-color-border-light,#e8ebf0); padding-bottom: 0.55rem; }
	.spd-tab { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.82rem; font-weight: 600; color: var(--theme-color-text-secondary,#45505f); background: var(--theme-color-background-secondary,#eef1f6); border: 1px solid transparent; border-radius: 7px; padding: 0.34rem 0.75rem; cursor: pointer; }
	.spd-tab i { font-size: 0.8rem; opacity: 0.85; }
	.spd-tab:hover { background: var(--theme-color-background-tertiary,#e3e8f0); }
	.spd-tab-active { color: #fff; background: var(--theme-color-brand-primary,#2e7d74); }
	.spd-tab-active:hover { background: var(--theme-color-brand-primary,#2e7d74); filter: brightness(0.97); }

	/* Custom preset controls */
	.spd-custom { margin: 0 0 0.85rem; padding: 0.7rem 0.8rem; background: var(--theme-color-background-secondary,#f4f6f9); border: 1px solid var(--theme-color-border-light,#e8ebf0); border-radius: 9px; }
	.spd-custom-row { display: flex; align-items: center; gap: 0.7rem; flex-wrap: wrap; }
	.spd-custom-label { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted,#7a8794); }
	.spd-cg-bar { display: inline-flex; gap: 0.3rem; flex-wrap: wrap; }
	.spd-cg { font-size: 0.8rem; font-weight: 600; color: var(--theme-color-text-secondary,#45505f); background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 6px; padding: 0.28rem 0.65rem; cursor: pointer; }
	.spd-cg:hover { background: var(--theme-color-background-tertiary,#eef1f6); }
	.spd-cg-active { color: #fff; background: var(--theme-color-brand-primary,#2e7d74); border-color: var(--theme-color-brand-primary,#2e7d74); }
	.spd-cg-active:hover { color: #fff; background: var(--theme-color-brand-primary,#2e7d74); filter: brightness(0.97); }
	.spd-custom-cols { margin-top: 0.6rem; }
	.spd-custom-cols > summary { cursor: pointer; font-size: 0.8rem; font-weight: 600; color: var(--theme-color-text-secondary,#45505f); list-style: none; display: inline-flex; align-items: center; gap: 0.4rem; user-select: none; }
	.spd-custom-cols > summary::-webkit-details-marker { display: none; }
	.spd-custom-count { min-width: 1.4rem; text-align: center; font-size: 0.7rem; font-weight: 700; color: #fff; background: var(--theme-color-text-secondary,#45505f); border-radius: 999px; padding: 0.04rem 0.45rem; }
	.spd-colgrid { display: grid; grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr)); gap: 0.25rem 0.9rem; margin-top: 0.6rem; }
	.spd-colpick { display: flex; align-items: center; gap: 0.4rem; font-size: 0.8rem; color: var(--theme-color-text-primary,#1f2733); cursor: pointer; }
	.spd-colpick input { cursor: pointer; }

	.spd-toolbar { display: flex; align-items: center; gap: 0.6rem; margin: 0 0 0.9rem; }
	.spd-search { position: relative; display: inline-block; flex: 1 1 auto; max-width: 26rem; }
	.spd-search-ic { position: absolute; left: 0.6rem; top: 50%; transform: translateY(-50%); color: var(--theme-color-text-muted,#9aa3af); font-size: 0.8rem; }
	.spd-search-input { width: 100%; font-size: 0.86rem; padding: 0.4rem 0.6rem 0.4rem 1.9rem; border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 7px; background: var(--theme-color-background-input,#fff); color: var(--theme-color-text-primary,#1f2733); }
	.spd-empty { padding: 2.4rem 1rem; text-align: center; color: var(--theme-color-text-muted,#8a94a3); font-size: 0.9rem; }
	.spd-empty.spd-error { color: var(--theme-color-status-danger,#c0392b); }
	.spd-term { font-weight: 600; background: var(--theme-color-background-secondary,#eef1f6); border-radius: 5px; padding: 0.05rem 0.4rem; color: var(--theme-color-text-secondary,#45505f); }
	.spd-clear { border: none; background: transparent; color: var(--theme-color-text-muted,#98a2b3); cursor: pointer; }
	.spd-clear:hover { color: var(--theme-color-status-danger,#c0392b); }
	.spd-spinner { display: inline-block; width: 1rem; height: 1rem; border: 2px solid var(--theme-color-border-medium,#d5dbe3); border-top-color: var(--theme-color-brand-primary,#2e7d74); border-radius: 50%; animation: spd-spin 0.7s linear infinite; vertical-align: -2px; }
	@keyframes spd-spin { to { transform: rotate(360deg); } }

	/* Default (embedded in a workspace tab): the table's own horizontal scroll is CONTAINED here, so a wide grid never
	   blows off the side of the workspace — it scrolls inside this box, and a "Full screen" pop-out opens the full-width
	   dashboard. In Dashboard Mode (full-width standalone screen) this is overridden to overflow:visible so the WINDOW is
	   the scroller and the column header can stick to the viewport (see .is-dashboard rules below). */
	.spd-tablewrap { width: 100%; overflow-x: auto; background: var(--theme-color-background-primary,#fff); border: 1px solid var(--theme-color-border-light,#e8ebf0); border-radius: 9px; }
	.spd-grid { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 0.85rem; table-layout: auto; }

	/* Line-item banner (full-span row) */
	.spd-li-cell { padding: 0; background: #c9f5f3; border-top: 6px solid var(--theme-color-background-primary,#fff); }
	.spd-li-row:first-child .spd-li-cell { border-top: none; }
	.spd-li-inner { display: flex; align-items: center; gap: 0.55rem; padding: 0.4rem 0.85rem; }
	.spd-li-ic { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 5px; background: #63b8b4; color: #fff; font-size: 0.72rem; flex: 0 0 auto; }
	.spd-li-name { font-weight: 700; color: #2c6f6b; font-size: 0.9rem; }
	.spd-li-code { font-size: 0.68rem; font-weight: 700; color: #2c6f6b; background: rgba(255,255,255,0.6); border-radius: 5px; padding: 0.05rem 0.4rem; }
	.spd-li-badge { margin-left: auto; min-width: 1.5rem; text-align: center; font-size: 0.72rem; font-weight: 700; color: #2c6f6b; background: rgba(255,255,255,0.7); border-radius: 999px; padding: 0.06rem 0.5rem; }

	/* Material sub-banner (full-span row, nested under a line item) */
	.spd-mat-cell { padding: 0; background: #fdeceb; border-top: 1px solid #f6d9d7; }
	.spd-mat-inner { display: flex; align-items: center; gap: 0.5rem; padding: 0.32rem 0.85rem 0.32rem 1.6rem; }
	.spd-mat-ic { display: inline-flex; align-items: center; justify-content: center; width: 19px; height: 19px; border-radius: 5px; background: #e37e7b; color: #fff; font-size: 0.64rem; flex: 0 0 auto; }
	.spd-mat-name { font-weight: 700; color: #ad4744; font-size: 0.82rem; }
	.spd-mat-code { font-size: 0.66rem; font-weight: 700; color: #ad4744; background: rgba(255,255,255,0.7); border: 1px solid #e37e7b; border-radius: 5px; padding: 0.02rem 0.4rem; }
	.spd-mat-badge { margin-left: auto; min-width: 1.4rem; text-align: center; font-size: 0.7rem; font-weight: 700; color: #ad4744; background: rgba(255,255,255,0.75); border-radius: 999px; padding: 0.04rem 0.5rem; }

	.spd-th { z-index: 3; text-align: left; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted,#6b7686); background: var(--theme-color-background-tertiary,#f4f6f9); padding: 0.45rem 0.7rem; border-bottom: 1px solid var(--theme-color-border-medium,#d5dbe3); box-shadow: 0 1px 0 var(--theme-color-border-light,#e8ebf0); white-space: nowrap; }
	/* Dashboard Mode (full-width standalone): the window is the scroller, so the grid grows down the page naturally and
	   the column header sticks below the app's fixed 3.25rem top navbar. (Embedded, the table box scrolls instead.) */
	.is-dashboard .spd-tablewrap { overflow: visible; }
	.is-dashboard .spd-th { position: sticky; top: 3.25rem; }
	/* Standalone: the chrome is inset by the page gutter (.spd-page), but the wide grid full-bleeds back to the browser
	   edges so it still uses the whole width. Scoped to .spd-page so the embedded copy (no .spd-page ancestor) is untouched. */
	.spd-page .spd-tablewrap { margin-left: calc(-1 * var(--spd-gutter)); margin-right: calc(-1 * var(--spd-gutter)); border-left: none; border-right: none; border-radius: 0; }
	.spd-th.spd-sortable { cursor: pointer; user-select: none; }
	.spd-th.spd-sortable:hover { color: var(--theme-color-text-secondary,#45505f); }
	.spd-td { padding: 0.45rem 0.7rem; border-bottom: 1px solid var(--theme-color-border-light,#eef1f5); color: var(--theme-color-text-primary,#1f2733); white-space: nowrap; vertical-align: top; }
	.spd-left { text-align: left; } .spd-right { text-align: right; font-variant-numeric: tabular-nums; } .spd-center { text-align: center; }
	.spd-muted { color: var(--theme-color-text-muted,#9aa3af); }
	.spd-ok { color: #2f9e6f; }

	/* The one "flex" column per preset absorbs slack and wraps, so the grid fills the browser width without a scroll. */
	.spd-th.spd-flex { width: 40%; }
	.spd-td.spd-flex { white-space: normal; min-width: 12rem; }
	.spd-td.spd-wrap { white-space: normal; }
	.spd-material .spd-matname { font-weight: 600; }
	.spd-matcode { display: inline-block; margin-left: 0.5rem; font-size: 0.68rem; font-weight: 700; color: #ad4744; border: 1px solid #e37e7b; border-radius: 5px; padding: 0.02rem 0.4rem; }
	.spd-fs-namecell .spd-fs-qr { display: block; font-weight: 700; font-family: var(--theme-font-family-mono,ui-monospace,SFMono-Regular,Menlo,monospace); font-size: 0.82rem; color: var(--theme-color-text-primary,#1f2733); }
	.spd-fs-namecell .spd-fs-name { display: block; font-size: 0.78rem; color: var(--theme-color-text-muted,#6b7686); }
	.spd-dispo-empty { color: var(--theme-color-text-muted,#9aa3af); font-style: italic; }
	.spd-action { width: 1%; }
	.spd-newbtn { display: inline-flex; align-items: center; gap: 0.3rem; white-space: nowrap; font-size: 0.76rem; font-weight: 600; color: #7a5b12; background: #efc865; border: none; border-radius: 6px; padding: 0.25rem 0.55rem; cursor: pointer; }
	.spd-newbtn:hover { filter: brightness(0.96); }

	/* Print button */
	.spd-print, .spd-popout, .spd-fullscreen-btn { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.82rem; font-weight: 600; color: var(--theme-color-text-secondary,#45505f); background: var(--theme-color-background-secondary,#eef1f6); border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 7px; padding: 0.36rem 0.7rem; cursor: pointer; text-decoration: none; white-space: nowrap; }
	.spd-print:hover, .spd-popout:hover, .spd-fullscreen-btn:hover { background: var(--theme-color-background-tertiary,#e3e8f0); }
	.spd-fullscreen-btn-on { color: #fff; background: var(--theme-color-brand-primary,#2e7d74); border-color: var(--theme-color-brand-primary,#2e7d74); }
	.spd-fullscreen-btn-on:hover { color: #fff; background: var(--theme-color-brand-primary,#2e7d74); filter: brightness(1.05); }
	/* Full-screen overlay: fills the whole browser view above the app nav (top navbar z-index 30, bottom bar 1050). The page
	   gutter var cascades in, and 'transform' composites the layer from creation so it paints immediately. */
	.spd.spd-fullscreen { position: fixed; inset: 0; z-index: 1100; overflow: auto; background: var(--theme-color-background-tertiary,#f3f4f6); padding: 1.1rem var(--spd-gutter, 2rem) 1.6rem; transform: translateZ(0); }

	/* Disposition inline editor (Failing Samples) */
	.spd-dispo-input { width: 100%; min-width: 12rem; font-size: 0.82rem; font-family: inherit; padding: 0.35rem 0.5rem; border: 1px solid var(--theme-color-border-medium,#d5dbe3); border-radius: 6px; resize: vertical; box-sizing: border-box; }
	.spd-dispo-actions { width: 1%; white-space: nowrap; }
	.spd-btn { font-size: 0.74rem; font-weight: 600; border: 1px solid transparent; border-radius: 6px; padding: 0.22rem 0.55rem; margin-left: 0.3rem; cursor: pointer; white-space: nowrap; }
	.spd-btn:first-child { margin-left: 0; }
	.spd-btn-warn { color: #7a5b12; background: #efc865; }
	.spd-btn-save { color: #fff; background: #2f9e6f; }
	.spd-btn-ghost { color: var(--theme-color-text-secondary,#45505f); background: var(--theme-color-background-secondary,#eef1f6); border-color: var(--theme-color-border-medium,#d5dbe3); }
	.spd-btn-del { color: #fff; background: #c0605d; }
	.spd-btn:hover { filter: brightness(0.96); }

	/* Print head — shown only when printing */
	.spd-printhead { display: none; }

	@media print {
		body:where(:has(#SamplePlanDashboard-Host)) * { visibility: hidden !important; }
		#SamplePlanDashboard-Host, #SamplePlanDashboard-Host * { visibility: visible !important; }
		#SamplePlanDashboard-Host { position: absolute; left: 0; top: 0; width: 100%; }
		.spd-head, .spd-tabs, .spd-toolbar, .spd-noprint { display: none !important; }
		.spd-printhead { display: block !important; visibility: visible !important; font-size: 1.05rem; font-weight: 700; margin: 0 0 0.6rem; color: #000; }
		.spd-tablewrap { overflow: visible !important; border: none !important; }
		.spd-grid { font-size: 0.72rem; }
		.spd-li-cell, .spd-mat-cell { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
	}
`;
